import React, {
    FC,
    ReactNode,
    useCallback,
    useContext,
    useMemo,
    useEffect,
    useRef,
    useState,
} from 'react';

import IconEditorMenuButton from '../../../../../components/buttons/iconEditorMenuButton';
//components
import styled from 'styled-components';

//constants
import aampeTheme from '../../../../../constants/aampeTheme';
import ICONS from '../../../../../constants/icons';
import { ToastStatus } from '../../../../../constants/toastMessage.constants';

//context
import { EditorContext } from '../../../../../context/EditorContext';

// helpers
import debounce from '../../../../../helpers/debounce';
import {
    buildUserAttributePlaceholder,
    insertAtCursor,
} from '../../../../../helpers/userAttributePlaceholder';

import useAttributesQuery from '../../../../../hooks/filters/useAttributesQuery';
import useAnalytics from '../../../../../hooks/helper/useAnalytics';
import useGetCurrentMessageContent from '../../../../../hooks/message-content/useGetCurrentMessageContent';
import useMessageContentGetAndUpdate from '../../../../../hooks/message-content/useMessageContentGetAndUpdate';
import useDatasetByIdQuery from '../../../../../hooks/useDatasetByIdQuery';
//hooks
import useOutsideClickDetector from '../../../../../hooks/useOutsideClickDetector';

// models
import { FieldType } from '../../../../../models/contact_attributes.model';
import { FormulaTypesData } from '../../../../../models/formula.model';
import { MessageContentUpdate } from '../../../../../models/message-content.model';

import { toastService } from '../../../../../services/toast.service';

import DatasetConfigurationalModalV1 from '../../../../../views/formulaCreate/formulaEditor/assistant/datasets/datasetConfigurationalModal/DatasetConfigurationalModalV1';

import DatasetElementView from '../DatasetElementView';
import AttributePlaceholderModal from '../SlateInput/AttributePlaceholder/AttributePlaceholderModal/AttributePlaceholderModal';

interface InputWrapperStyleProps {
    isFocused: boolean;
}
interface DeeplinkComponentV2Props {
    isDisabled?: boolean;
}

const DeeplinkComponentV2: FC<DeeplinkComponentV2Props> = ({ isDisabled }) => {
    const {
        editorDetails: { formula },
    } = useContext(EditorContext);

    const { currentMessageContentId } = useGetCurrentMessageContent(
        formula?.id,
    );

    const [
        showDatasetConfigurationalModal,
        setShowDatasetConfigurationalModal,
    ] = useState<boolean>(false);

    const [showAttributePlaceholderModal, setShowAttributePlaceholderModal] =
        useState<boolean>(false);

    const { data: dataset } = useDatasetByIdQuery(formula?.dataset_id);
    const { data: attributesData } = useAttributesQuery({
        is_active: true,
        sort_by_name: true,
        attribute_type: FieldType.STR,
    });
    const { track } = useAnalytics();
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const inputRef = useOutsideClickDetector(() => {
        setIsFocused(false);
    });
    const inputElementRef = useRef<HTMLInputElement>(null);

    const [staticField, setStaticField] = useState<string | null>(
        formula?.deeplink!,
    );
    const [datasetFieldId, setDatasetFieldId] = React.useState<number | null>(
        formula?.deeplink_dataset_field_id!
            ? Number(formula.deeplink_dataset_field_id.split('.')[0])
            : null,
    );

    const setFocus = useCallback(() => {
        setIsFocused(true);
    }, []);

    const handleClickDatasetIcon = useCallback(() => {
        setShowDatasetConfigurationalModal(true);
    }, []);

    const handleClickAttributeIcon = useCallback(() => {
        setShowAttributePlaceholderModal(true);
    }, []);

    const showUserAttributePlaceholderIcon = useMemo<boolean>(
        () => !!attributesData?.items?.length,
        [attributesData],
    );

    const messageContentUpdate = useMessageContentGetAndUpdate();

    const handleUpdateMessageContent = useCallback(
        (payload: Partial<MessageContentUpdate>) => {
            if (currentMessageContentId) {
                messageContentUpdate.mutate({
                    ...payload,
                    id: currentMessageContentId!,
                });
                return;
            }
            toastService.showNotificationToast(
                ToastStatus.Error,
                'Message content not found',
            );
        },
        [currentMessageContentId, messageContentUpdate],
    );

    const handleDeeplinkAdd = debounce(() => {
        return handleUpdateMessageContent({
            deeplink: staticField,
            deeplink_dataset_field_id: null,
        });
    }, 2500);

    // The deeplink is a plain string that composer-backend forwards verbatim,
    // so the placeholder is inserted as wire-form text rather than as a
    // structured node like the message body uses.
    const insertUserAttributePlaceholder = useCallback(
        (defaultValue: string, userAttribute: string) => {
            const currentValue = datasetFieldId === null ? staticField : '';
            const newValue = insertAtCursor(
                currentValue,
                buildUserAttributePlaceholder(userAttribute, defaultValue),
                inputElementRef.current?.selectionStart,
            );
            setDatasetFieldId(null);
            setStaticField(newValue);
            handleUpdateMessageContent({
                deeplink: newValue,
                deeplink_dataset_field_id: null,
            });
            track('formula_attribute_placeholder_inserted', {
                formula_id: formula?.id,
                field: 'deeplink',
            });
        },
        [
            datasetFieldId,
            staticField,
            handleUpdateMessageContent,
            track,
            formula,
        ],
    );

    const deeplinkInputValue = useMemo<string | null>(() => {
        if (dataset && datasetFieldId !== null) {
            return '';
        }
        return staticField;
    }, [dataset, datasetFieldId, staticField]);

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Backspace' && datasetFieldId !== null) {
                event.preventDefault();
                setStaticField('');
                setDatasetFieldId(null);
                handleUpdateMessageContent({
                    deeplink: null,
                    deeplink_dataset_field_id: null,
                });
            }
        },
        [datasetFieldId, handleUpdateMessageContent],
    );

    const isDatasetFieldId = useMemo<boolean>(
        () => datasetFieldId === 0 || !!datasetFieldId,
        [datasetFieldId],
    );

    const AampeToolTipTitle = useMemo<ReactNode | undefined>(() => {
        if (dataset && datasetFieldId !== null) {
            const field = dataset.fields[datasetFieldId];
            return (
                <ToolTipText>
                    <Text>Dataset name: {dataset.name}</Text>
                    <Text>Field name: {field.name}</Text>
                    <Text>Sample: {field.sample}</Text>
                </ToolTipText>
            );
        }
        return undefined;
    }, [dataset, datasetFieldId]);

    useEffect(() => {
        setStaticField(formula?.deeplink!);
        setDatasetFieldId(
            formula?.deeplink_dataset_field_id!
                ? Number(formula.deeplink_dataset_field_id.split('.')[0])
                : null,
        );
    }, [formula]);

    if (
        formula?.message_type &&
        FormulaTypesData[formula?.message_type!]?.supportDeeplink
    ) {
        return (
            <Wrapper>
                <DeeplinkText>Deeplink</DeeplinkText>
                <SlateInputBox
                    ref={inputRef}
                    isFocused={isFocused}
                    onClick={setFocus}
                >
                    {isDatasetFieldId && formula?.dataset_id ? (
                        <StyledDatasetElementView
                            handleClick={() =>
                                isDisabled ? {} : handleClickDatasetIcon()
                            }
                            aampeToolTipTitle={AampeToolTipTitle!}
                            datasetFieldSample={
                                dataset?.fields[datasetFieldId!]?.sample
                            }
                        />
                    ) : null}

                    <DeeplinkInput
                        ref={inputElementRef}
                        disabled={isDisabled}
                        isDatasetField={isDatasetFieldId}
                        value={deeplinkInputValue ?? ''}
                        placeholder={isDatasetFieldId ? '' : 'Add deeplink URL'}
                        onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                            setDatasetFieldId(null);
                            setStaticField(event.target.value);
                            handleDeeplinkAdd();
                        }}
                        onKeyDown={handleKeyDown}
                    />
                    {isFocused &&
                        !isDisabled &&
                        (showUserAttributePlaceholderIcon ||
                            formula?.dataset_id) && (
                            <SectionLowerMenu>
                                {showUserAttributePlaceholderIcon && (
                                    <IconEditorMenuButton
                                        Icon={ICONS.attributePlaceholder}
                                        isDisabled={false}
                                        action={handleClickAttributeIcon}
                                        tooltip="Insert user attribute placeholder"
                                    />
                                )}
                                {formula?.dataset_id && (
                                    <IconEditorMenuButton
                                        Icon={ICONS.datasets}
                                        isDisabled={false}
                                        action={handleClickDatasetIcon}
                                        tooltip="Insert Dataset"
                                    />
                                )}
                            </SectionLowerMenu>
                        )}
                </SlateInputBox>
                {showAttributePlaceholderModal && (
                    <AttributePlaceholderModal
                        handleClose={() =>
                            setShowAttributePlaceholderModal(false)
                        }
                        insertUserAttributePlaceholder={
                            insertUserAttributePlaceholder
                        }
                    />
                )}
                {showDatasetConfigurationalModal && (
                    <DatasetConfigurationalModalV1
                        handleClose={() => {
                            setShowDatasetConfigurationalModal(false);
                        }}
                        showColumnSelect
                        handleColumnSelect={(id: string) => {
                            setStaticField('');
                            setDatasetFieldId(Number(id));
                            handleUpdateMessageContent({
                                deeplink: null,
                                deeplink_dataset_field_id: id,
                            });
                        }}
                        selectedFieldId={
                            datasetFieldId === undefined ||
                            datasetFieldId === null
                                ? undefined
                                : datasetFieldId?.toString()
                        }
                        setSelectedFieldId={(id: string) => {
                            setDatasetFieldId(Number(id));
                        }}
                    />
                )}
            </Wrapper>
        );
    }
    return null;
};

const StyledDatasetElementView = styled(DatasetElementView)({
    position: 'absolute',
    left: '5px',
    top: '5px',
    bottom: '5px',
    right: '5px',
    borderRadius: aampeTheme.borderRadius.default,
    backgroundColor: aampeTheme.colors.interactiveWG01,
    padding: aampeTheme.spacing4(1, 8, 1, 1),
    color: aampeTheme.colors.neutral09,
    lineHeight: '24px',
    fontSize: aampeTheme.size.font.default,
    fontWeight: 400,
    minWidth: '30px',
    border: `1px solid ${aampeTheme.colors.solidA02}`,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
});

const Text = styled.div({
    fontSize: aampeTheme.size.font.default,
    lineHeight: '24px',
    fontWeight: 400,
});

const ToolTipText = styled.div({
    display: 'flex',
    flexDirection: 'column',
    padding: aampeTheme.spacing4(2),
});

const Wrapper = styled.div({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: aampeTheme.colors.backgroundWG02,
    padding: aampeTheme.spacing4(4, 0, 0, 0),
});

const DeeplinkText = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing4(2),
    marginBottom: aampeTheme.spacing4(2),
    fontSize: aampeTheme.size.font.default,
    fontWeight: 500,
    lineHeight: '20px',
    color: aampeTheme.colors.gray800,
    textTransform: 'capitalize',
});

const DeeplinkInput = styled.input.withConfig({
    shouldForwardProp: (prop) => prop !== 'isDatasetField',
})<{ isDatasetField: boolean }>(({ isDatasetField }) => ({
    color: aampeTheme.colors.neutral09,
    justifySelf: 'center',
    outline: 'none',
    fontSize: aampeTheme.size.font.default,
    lineHeight: '18px',
    fontWeight: 400,
    padding: isDatasetField
        ? aampeTheme.spacing4(2, 2, 2, 10)
        : aampeTheme.spacing4(2),
    wordBreak: 'break-word',
    minHeight: '37px',
    border: 'none',
    '&::placeholder': {
        color: aampeTheme.colors.textAccessibleWG01,
        opacity: 0.8,
    },
}));

const SectionLowerMenu = styled.div({
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'flex-end',
    justifyContent: 'end',
    gap: aampeTheme.spacing4(2),
    padding: aampeTheme.spacing4(1),
    userSelect: 'none',
    position: 'absolute',
    right: '10px',
    top: '8px',
});

const SlateInputBox = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'isFocused',
})<InputWrapperStyleProps>((props) => ({
    borderRadius: aampeTheme.borderRadius.default,
    border: `1px solid  ${
        props.isFocused
            ? aampeTheme.colors.solidA02
            : aampeTheme.colors.bordersWG01
    }`,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '100%',
    background: aampeTheme.colors.white,
    padding: aampeTheme.spacing4(1),
    position: 'relative',
}));

export default DeeplinkComponentV2;
